import React, {
  useState,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import Popover from '@mui/material/Popover';
import compact from 'lodash/compact';
import noop from 'lodash/noop';
import isNil from 'lodash/isNil';
import {
  pluralize,
} from '@populateui/utils';
import {
  formatDate,
} from 'utils/date';
import MultiChoiceGroup from 'core/components/MultiChoiceGroup';
import {
  daysOfWeek,
} from 'core/constants';
import Editable from 'core/components/Editable';
import Button from 'pages/Dashboard/components/Button';
import Row from 'pages/Dashboard/components/Row';
import {
  eventRecurrenceMap,
} from 'pages/Dashboard/pages/Appointments/pages/List/utils/helper';
import {
  AppointmentForm,
} from 'pages/Dashboard/pages/Appointments/pages/List/components/Details/index';
import {
  CommonOption,
  Recurrence,
} from 'pages/Dashboard/pages/Appointments/pages/List/types/appointment';
import EndsOnConditionFields from 'pages/Dashboard/pages/Appointments/pages/List/components/EndsOnConditionFields';
import RecurrenceFields from 'pages/Dashboard/pages/Appointments/pages/List/components/RecurrenceFields';

type Props = {
  onSave: (formData: AppointmentForm) => Promise<void>;
  data: Partial<AppointmentForm>;
};

function getEndCondition({ endsOn, count }: Pick<Recurrence, 'endsOn' | 'count'>) {
  const endsOnCondition = !isNil(endsOn);
  const countCondition = !isNil(count);
  const result = [];

  if (endsOnCondition) {
    result.push('On', formatDate(endsOn));
  } else if (countCondition) {
    result.push('After', pluralize('occurrence', Number(count)));
  } else {
    result.push('Never');
  }

  return compact(result).join(' ');
}

type RecurrenceLabelProps = Omit<Recurrence, 'repeatsOn'> & {
  repeatsOn?: CommonOption[];
};

function RecurrenceLabel({
  interval,
  repeatType,
  repeatsOn,
}: RecurrenceLabelProps) {
  const type = repeatType?.id ?? '';
  return (
    <div className="flex w-full gap-2 items-center">
      {pluralize(eventRecurrenceMap[type] ?? '', interval ?? 0)}
      {type === 'weekly' && (
        <MultiChoiceGroup<CommonOption>
          readonly
          value={repeatsOn ?? []}
          onChange={noop}
          options={daysOfWeek}
          getLabel={({ label }) => label}
          getId={({ id }) => id}
        />
      )}
    </div>
  );
}

export default function RecurringEditControls({ onSave, data }: Props) {
  const {
    handleSubmit,
    getFieldState,
    formState,
    setValue,
    watch,
  } = useFormContext<AppointmentForm>();
  const {
    isValid,
    isSubmitting,
    errors,
  } = formState;

  const [repeatContainer, setRepeatContainer] = useState<HTMLDivElement | null>(null);
  const [endsConditionContainer, setEndsConditionContainer] = useState<HTMLDivElement | null>(null);
  const recurrence = watch('recurrence');

  const { isDirty: isRecurrenceChanged } = getFieldState('recurrence', formState);

  return (
    <>
      <Row
        title="Every"
        className="row-container px-4"
      >
        <div className="flex items-center">
          <Editable
            idleView={(
              <div className="w-min">
                <div className="truncate text-gray-500 items-center space-x-1 flex w-full">
                  <div className="truncate w-max break-words max-w-full">
                    <RecurrenceLabel {...recurrence} />
                  </div>
                </div>
              </div>
            )}
            tooltip="Edit recurrence"
            isLoading={isSubmitting && isRecurrenceChanged}
            readonly={isSubmitting}
            onSubmit={handleSubmit(onSave)}
            classes={{ editableRoot: '!text-black' }}
            onCancel={() => {
              setValue('recurrence.interval', data?.recurrence?.interval);
              setValue('recurrence.repeatType', data?.recurrence?.repeatType);
              setValue('recurrence.repeatsOn', data?.recurrence?.repeatsOn);
            }}
            onShowControl={(e) => {
              const element = e.currentTarget?.closest('.row-container') as HTMLDivElement;
              setRepeatContainer(element);
            }}
          >
            {(onSubmit, onCancel) => (
              <Popover
                open={!isNil(repeatContainer)}
                onClose={() => {
                  setRepeatContainer(null);
                  onCancel();
                }}
                classes={{
                  paper: '!w-[608px] !overflow-y-visible !overflow-x-visible',
                }}
                anchorEl={repeatContainer}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <div className="flex flex-col p-2 gap-3">
                  <RecurrenceFields />
                  <div className="flex flex-row gap-3">
                    <Button
                      onClick={() => { onSubmit(); }}
                      color="primary"
                      variant="contained"
                      disabled={!isValid && !isNil(errors)}
                    >
                      Update
                    </Button>
                    <Button variant="outlined" onClick={onCancel}>
                      Close
                    </Button>
                  </div>
                </div>
              </Popover>
            )}
          </Editable>
        </div>
      </Row>
      <Row
        title="Ends"
        className="row-container px-4"
      >
        <div className="flex items-center">
          <Editable
            idleView={(
              <div className="w-min">
                <div className="truncate text-gray-500 items-center space-x-1 flex w-full">
                  <span className="truncate w-max mb-1 break-words max-w-full">
                    {getEndCondition(recurrence ?? {})}
                  </span>
                </div>
              </div>
            )}
            tooltip="Edit end condition"
            isLoading={isSubmitting && isRecurrenceChanged}
            readonly={isSubmitting}
            onSubmit={handleSubmit(onSave)}
            classes={{ editableRoot: '!text-black' }}
            onCancel={() => {
              setValue('recurrence.endsOn', data?.recurrence?.endsOn);
              setValue('recurrence.count', data?.recurrence?.count);
            }}
            onShowControl={(e) => {
              const element = e.currentTarget.closest('.row-container') as HTMLDivElement;
              setEndsConditionContainer(element);
            }}
          >
            {(onSubmit, onCancel) => (
              <Popover
                open={!isNil(endsConditionContainer)}
                onClose={() => {
                  setEndsConditionContainer(null);
                  onCancel();
                }}
                classes={{
                  paper: '!w-[608px] !overflow-y-visible !overflow-x-visible',
                }}
                anchorEl={endsConditionContainer}
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
              >
                <div className="flex flex-col p-2 gap-3">
                  <EndsOnConditionFields withLabel={false} />
                  <div className="flex flex-row gap-3">
                    <Button
                      onClick={() => { onSubmit(); }}
                      color="primary"
                      variant="contained"
                      disabled={!isValid}
                    >
                      Update
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={onCancel}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </Popover>
            )}
          </Editable>
        </div>
      </Row>
    </>
  );
}
